import React, { useState } from "react"
import PropTypes from "prop-types"

import { partidasVivas } from "../useOrdersCreate"
import { StarsMini } from "./Stars"

const numberFormat = new Intl.NumberFormat("es-MX", {
  minimumFractionDigits: 2,
})

const claseEstado = estado =>
  estado === "requested" ? "ped-badge is-pendiente" : "ped-badge is-listo"

const RequestedOrdersTable = ({
  order,
  isSaving,
  editingItem,
  onStartEdit,
  onCancelEdit,
  onUpdateItem,
  onDeleteItem,
}) => {
  const [draft, setDraft] = useState({ quantity: "", observations: "" })
  const [confirmando, setConfirmando] = useState(null)

  const partidas = partidasVivas(order)
  if (partidas.length === 0) return null

  const empezarEdicion = partida => {
    setConfirmando(null)
    setDraft({
      quantity: String(partida.quantity ?? ""),
      observations: partida.observations || "",
    })
    onStartEdit(partida.id)
  }

  return (
    <div className="ped-tabla">
      <div className="ped-tabla-scroll">
        <div className="ped-tabla-head">
          <span>Producto</span>
          <span>Recepción</span>
          <span>Calidad</span>
          <span className="ped-right">Peso</span>
          <span className="ped-right">Pedido</span>
          <span className="ped-right">P. Unit.</span>
          <span className="ped-right">Total</span>
          <span />
        </div>

        {partidas.map(partida => {
          const editando = editingItem === partida.id
          const precio = partida.delivered
            ? partida.unit_price_shipped
            : partida.unit_price

          return (
            <div
              key={partida.id}
              className={`ped-tabla-fila${editando ? " is-editing" : ""}`}
            >
              <div className="ped-tabla-row">
                <div className="ped-celda-producto">
                  <span className="ped-marca" />
                  <div className="ped-celda-producto-texto">
                    <div className="ped-celda-producto-titulo">
                      <span className="ped-producto-nombre">
                        {partida.product}
                      </span>
                      <span className={claseEstado(partida.status)}>
                        {partida.status_string}
                      </span>
                    </div>
                    <div className="ped-celda-sub">
                      {partida.delivered ? "Con entrega" : "En granja"}
                    </div>
                  </div>
                </div>

                <div className="ped-celda-recepcion">
                  <div className="ped-celda-region">
                    {partida.delivered_region} {partida.delivered_subregion}
                  </div>
                  {partida.delivery_date && (
                    <div className="ped-celda-sub ped-num">
                      {partida.delivery_date}
                    </div>
                  )}
                  {partida.observations && (
                    <div className="ped-celda-sub">{partida.observations}</div>
                  )}
                </div>

                <div className="ped-celda-calidad">
                  <StarsMini label="PIG" value={partida.quality} />
                  <StarsMini label="CAL" value={partida.quality_2} />
                  <StarsMini label="SAL" value={partida.quality_3} />
                </div>

                <div className="ped-right ped-num ped-celda-suave">
                  {numberFormat.format(partida.weight_prom)}
                  <span className="ped-unidad">kg</span>
                </div>
                <div className="ped-right ped-num ped-celda-cantidad">
                  {partida.quantity}
                  <span className="ped-unidad">{partida.unit}</span>
                </div>
                <div className="ped-right ped-num ped-celda-suave">
                  ${numberFormat.format(precio)}
                </div>
                <div className="ped-right ped-num ped-celda-total">
                  ${numberFormat.format(partida.total)}
                </div>

                <div className="ped-celda-acciones">
                  <button
                    type="button"
                    className="ped-btn-icono"
                    title="Editar partida"
                    disabled={isSaving}
                    onClick={() => empezarEdicion(partida)}
                  >
                    <i className="bx bx-edit-alt" />
                  </button>
                  <button
                    type="button"
                    className="ped-btn-icono is-borrar"
                    title="Eliminar partida"
                    disabled={isSaving}
                    onClick={() => {
                      onCancelEdit()
                      setConfirmando(partida.id)
                    }}
                  >
                    <i className="bx bx-trash" />
                  </button>
                </div>
              </div>

              {confirmando === partida.id && (
                <div className="ped-confirmar">
                  <span>
                    ¿Eliminar la partida de <b>{partida.product}</b>? La
                    cancelación no se puede deshacer.
                  </span>
                  <div className="ped-confirmar-acciones">
                    <button
                      type="button"
                      className="ped-btn-ghost"
                      onClick={() => setConfirmando(null)}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="ped-btn-peligro"
                      disabled={isSaving}
                      onClick={async () => {
                        setConfirmando(null)
                        await onDeleteItem(partida.id)
                      }}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              )}

              {editando && (
                <div className="ped-editor">
                  <div className="ped-editor-campos">
                    <div>
                      <label
                        className="ped-label"
                        htmlFor={`ped-edit-cant-${partida.id}`}
                      >
                        Ordenar (Pzas)
                      </label>
                      <input
                        id={`ped-edit-cant-${partida.id}`}
                        className="ped-field ped-field-cantidad ped-num"
                        type="number"
                        min="1"
                        value={draft.quantity}
                        onChange={event =>
                          setDraft({ ...draft, quantity: event.target.value })
                        }
                        onWheel={event => event.target.blur()}
                      />
                    </div>
                    <div className="ped-editor-obs">
                      <label
                        className="ped-label"
                        htmlFor={`ped-edit-obs-${partida.id}`}
                      >
                        Observaciones
                      </label>
                      <textarea
                        id={`ped-edit-obs-${partida.id}`}
                        className="ped-field"
                        rows={2}
                        value={draft.observations}
                        onChange={event =>
                          setDraft({
                            ...draft,
                            observations: event.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="ped-editor-acciones">
                    <button
                      type="button"
                      className="ped-btn-ghost"
                      onClick={onCancelEdit}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="ped-btn-save"
                      disabled={isSaving || !(parseInt(draft.quantity, 10) > 0)}
                      onClick={() =>
                        onUpdateItem(partida.id, {
                          quantity: parseInt(draft.quantity, 10),
                          observations: draft.observations,
                        })
                      }
                    >
                      {isSaving ? "Guardando…" : "Guardar cambios"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

RequestedOrdersTable.propTypes = {
  order: PropTypes.object,
  isSaving: PropTypes.bool,
  editingItem: PropTypes.number,
  onStartEdit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  onUpdateItem: PropTypes.func.isRequired,
  onDeleteItem: PropTypes.func.isRequired,
}

export default RequestedOrdersTable
